import { displayFromRaw18 } from "./displayDecimals";
import { knownPrice } from "./unknownNumbers";

/**
 * Guards for mark / index prices, which the backend now leaves null (REST) or
 * omits (websocket) when it does not know them, and which no longer stand in
 * for each other.
 */

/**
 * A check measured against the mark (e.g. a stop trigger must sit above it).
 * Without a known mark the check cannot run: it is skipped, never evaluated
 * against 0, which would pass every "above" check and fail every "below" one.
 */
export const passesMarkCheck = (
  value: string,
  mark: string | number | null | undefined,
  direction: "atOrAbove" | "atOrBelow"
): boolean => {
  const m = knownPrice(mark);
  if (m === undefined) return true;
  const v = parseFloat(value);
  return direction === "atOrAbove" ? v >= m : v <= m;
};

/**
 * A websocket price in display units, or `undefined` when the frame omitted it
 * (or sent a zero, which is not a quote). Never "0".
 */
export const knownDisplayPrice = (
  raw: string | number | null | undefined
): string | undefined => {
  const value = displayFromRaw18(raw);
  return knownPrice(value) !== undefined ? String(value) : undefined;
};

/**
 * The first KNOWN price among candidates for the SAME quantity (the live mark,
 * then the REST mark). Never mix kinds here: index must not stand in for mark.
 */
export const firstKnownPrice = <T extends string | number | null | undefined>(
  ...candidates: T[]
): T | undefined => candidates.find((p) => knownPrice(p) !== undefined);
